import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fulfillmentLifecycleApi } from 'api/fulfillmentLifecycle';

/**
 * Default summary state for the dashboard.
 * Hoisted outside the hook to prevent recreation on each render.
 */
const DEFAULT_SUMMARY = {
    totalRoutes: 0,
    totalShipments: 0,
    critical: 0,
    atRisk: 0,
    onTrack: 0,
    facilityThroughput: 0,
    labelAgingCount: 0,
    packingAgingCount: 0,
};

/**
 * Custom hook for fetching fulfillment lifecycle dashboard data.
 *
 * @param {number|null} facilityId - The facility ID to query
 * @param {string} operatingDate - The operating date in YYYY-MM-DD format
 * @param {Object} filters - Optional filters
 * @param {string[]} [filters.orderSources] - Filter by order source
 * @param {string[]} [filters.deliveryTypes] - Filter by delivery type
 * @returns {Object} - { routes, summary, loading, error, refetch }
 */
const useFulfillmentLifecycleData = (facilityId, operatingDate, filters = {}) => {
    const [routes, setRoutes] = useState([]);
    const [summary, setSummary] = useState(DEFAULT_SUMMARY);
    const [loading, setLoading] = useState(() => Boolean(facilityId && operatingDate));
    const [error, setError] = useState(null);
    const [refreshFailureCount, setRefreshFailureCount] = useState(0);
    const [hasSuccessfulSnapshot, setHasSuccessfulSnapshot] = useState(false);
    const [lastSuccessfulAt, setLastSuccessfulAt] = useState(null);
    const abortControllerRef = useRef(null);
    const hasSuccessfulSnapshotRef = useRef(false);

    // Normalize + stabilize API filter values to avoid duplicate fetches caused by array ordering.
    const normalizedOrderSources = useMemo(
        () => [...new Set(filters.orderSources || [])].sort((a, b) => a.localeCompare(b)),
        [filters.orderSources],
    );
    const normalizedDeliveryTypes = useMemo(
        () => [...new Set(filters.deliveryTypes || [])].sort((a, b) => a.localeCompare(b)),
        [filters.deliveryTypes],
    );
    const orderSourcesKey = useMemo(() => normalizedOrderSources.join(','), [normalizedOrderSources]);
    const deliveryTypesKey = useMemo(() => normalizedDeliveryTypes.join(','), [normalizedDeliveryTypes]);

    useEffect(() => {
        hasSuccessfulSnapshotRef.current = hasSuccessfulSnapshot;
    }, [hasSuccessfulSnapshot]);

    const fetchData = useCallback(async () => {
        if (!facilityId || !operatingDate) {
            setRoutes([]);
            setSummary(DEFAULT_SUMMARY);
            setError(null);
            setRefreshFailureCount(0);
            setHasSuccessfulSnapshot(false);
            setLastSuccessfulAt(null);
            setLoading(false);
            return;
        }

        // Cancel previous request if still in flight
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setError(null);

        try {
            const response = await fulfillmentLifecycleApi.getStatus({
                facilityId,
                operatingDate,
                orderSources: normalizedOrderSources,
                deliveryTypes: normalizedDeliveryTypes,
                signal: abortController.signal,
            });

            // Check if request was aborted
            if (abortController.signal.aborted) {
                return;
            }

            if (response.status) {
                setRoutes(response.data?.routes || []);
                setSummary({
                    ...DEFAULT_SUMMARY,
                    ...(response.data?.summary || {}),
                });
                setRefreshFailureCount(0);
                setHasSuccessfulSnapshot(true);
                setLastSuccessfulAt(Date.now());
            } else {
                if (!hasSuccessfulSnapshotRef.current) {
                    setRoutes([]);
                    setSummary(DEFAULT_SUMMARY);
                    setRefreshFailureCount(0);
                } else {
                    setRefreshFailureCount((prev) => prev + 1);
                }
                setError(response.msg || 'Failed to fetch dashboard data');
            }
        } catch (err) {
            // Ignore aborted requests
            if (err.name === 'AbortError') {
                return;
            }
            if (!hasSuccessfulSnapshotRef.current) {
                setRoutes([]);
                setSummary(DEFAULT_SUMMARY);
                setRefreshFailureCount(0);
            } else {
                setRefreshFailureCount((prev) => prev + 1);
            }
            setError(err.message || 'An unexpected error occurred');
        } finally {
            // Only update loading state if request wasn't aborted
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    }, [facilityId, operatingDate, orderSourcesKey, deliveryTypesKey]);

    useEffect(() => {
        fetchData();

        // Cleanup: abort request on unmount or when dependencies change
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchData]);

    return {
        routes,
        summary,
        loading,
        error,
        refetch: fetchData,
        refreshFailureCount,
        hasSuccessfulSnapshot,
        lastSuccessfulAt,
    };
};

export { useFulfillmentLifecycleData };
